import { useState } from 'react';
import { TextField, Container, Box, Button, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import {
  BASIC_INFO_AREA_DOWN,
  BASIC_INFO_AREA_UP,
  BASIC_INFO_BUTTON_DOWN,
  BASIC_INFO_BUTTON_UP,
  HEADER_HEIGHT,
} from '../../_constants/styleConst';
import { useSelector } from 'react-redux';
import { RootState } from '../../stores/store';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../_utils/utils';

const slideUp = keyframes`
  from {
    max-height: ${BASIC_INFO_AREA_UP};
    transform: translateY(0);
  }
  to {
    max-height: ${BASIC_INFO_AREA_DOWN};
    transform: translateY(-100%);
  }
`;

const slideDown = keyframes`
  from {
    max-height: ${BASIC_INFO_AREA_DOWN};
    transform: translateY(-100%);
  }
  to {
    max-height: ${BASIC_INFO_AREA_UP};
    transform: translateY(0);
  }
`;

const buttonSlideUp = keyframes`
  from {
    top: ${BASIC_INFO_BUTTON_UP};
  }
  to {
    top: ${BASIC_INFO_BUTTON_DOWN};
  }
`;

const buttonSlideDown = keyframes`
  from {
    top: ${BASIC_INFO_BUTTON_DOWN};
  }
  to {
    top: ${BASIC_INFO_BUTTON_UP};
  }
`;

interface AnimationProps {
  isHidden: boolean;
  timeAnimation: string;
}

const AnimatedContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== 'isHidden' && prop !== 'timeAnimation',
})<AnimationProps>(({ isHidden, timeAnimation }) => ({
  overflow: 'hidden',
  animation: `${isHidden ? slideUp : slideDown} ${timeAnimation} ease-in-out forwards`,
  position: 'relative',
  maxHeight: `${BASIC_INFO_AREA_UP}`,
}));

const AnimatedButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isHidden' && prop !== 'timeAnimation',
})<AnimationProps>(({ isHidden, timeAnimation }) => ({
  color: '#000',
  padding: '5px 8px',
  backgroundColor: '#f2f2f2',
  position: 'absolute',
  right: '5%',
  borderRadius: '0 0 4px 4px',
  boxShadow: 'none',
  animation: `${isHidden ? buttonSlideUp : buttonSlideDown} ${timeAnimation} ease-in-out forwards`,
  '&:hover': {
    boxShadow: 'none',
  },
}));

const BasicInfo: React.FC = () => {
  const theme = useTheme();
  const TextFieldBasicInfo = styled(TextField)({
    width: '100%',
    '& .MuiInputBase-input': {
      fontSize: theme.typography.caption.fontSize,
      padding: '5px',
      pointerEvents: 'none',
      caretColor: 'transparent',
    },
    '& .MuiInputLabel-root': {
      marginTop: '3px',
      fontSize: theme.typography.caption.fontSize,
    },
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.background.default,
      borderRadius: '4px',
    },
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      pointerEvents: 'none',
    },
  });
  const { t } = useTranslation();
  const [isHiddenContainer, setIsHiddenContainer] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  const { userData } = useSelector((state: RootState) => state.userInfo);

  const getTimeAnimation = () => {
    return isFirstLoad ? '0s' : '0.5s';
  };

  const toggleContainerVisibility = () => {
    if (repairRequest) {
      setIsHiddenContainer(!isHiddenContainer);
    }
    if (isFirstLoad) {
      setIsFirstLoad(false);
    }
  };

  return (
    <Box flex="0 1 auto" sx={{ marginTop: `${HEADER_HEIGHT}` }}>
      <AnimatedContainer
        timeAnimation={getTimeAnimation()}
        maxWidth={false}
        sx={{
          position: 'relative',
          backgroundColor: '#F2F2F2',
        }}
        isHidden={isHiddenContainer}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          p={2}
          padding="10px 0px 10px 0px"
        >
          {repairRequest && (
            <>
              {/* 1行目 */}
              <Box display="flex" gap={2} paddingTop={0.5}>
                <TextFieldBasicInfo
                  label={t('LBL_RMA_NO')}
                  value={repairRequest.rmaNo}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_SERIAL_NO')}
                  value={repairRequest.serialNo}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_MODEL')}
                  value={repairRequest.model}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_SCOPE_CONNECTION_COUNT')}
                  value={repairRequest.scopeConnectionCount ?? ''}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_OWNERSHIP')}
                  value={repairRequest.ownership}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_SERVICE_TYPE')}
                  value={repairRequest.serviceType}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Box>
              {/* 2行目 */}
              <Box display="flex" gap={2} paddingTop={0}>
                <TextFieldBasicInfo
                  label={t('LBL_INSTALL_DATE')}
                  value={formatDate(
                    repairRequest.installDate,
                    userData.dateFormat,
                  )}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_CUSTOMER')}
                  value={repairRequest.customerName}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_ORIGINAL_CUSTOMER')}
                  value={repairRequest.originalCustomer}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_CUSTOMER_PRIORITY')}
                  value={repairRequest.customerPriority}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_WARRANTY_END_DATE')}
                  value={formatDate(
                    repairRequest.warrantyEndDate,
                    userData.dateFormat,
                  )}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_CONTRACT_NUMBER')}
                  value={repairRequest.contract}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextFieldBasicInfo
                  label={t('LBL_CONTRACT_END_DATE')}
                  value={formatDate(
                    repairRequest.contractEndDate,
                    userData.dateFormat,
                  )}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Box>
              {/* 3行目 */}
              <Box display="flex" gap={2} paddingTop={0}>
                <TextFieldBasicInfo
                  label={t('LBL_CUSTOMER_COMMENT')}
                  value={repairRequest.customerComment}
                  minRows={2}
                  multiline
                  slotProps={{
                    input: {
                      sx: {
                        padding: '0px',
                      },
                    },
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </AnimatedContainer>
      <AnimatedButton
        timeAnimation={getTimeAnimation()}
        variant="contained"
        size="small"
        isHidden={isHiddenContainer}
        onClick={toggleContainerVisibility}
      >
        {`${isHiddenContainer ? '▼ ' : '▲ '} ${t('BTN_BASIC_INFO')}`}
      </AnimatedButton>
    </Box>
  );
};

export default BasicInfo;
